import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';

import {
    ClockService,
    DynamicHtmlDirective,
    DynamicLayoutService,
    HtmlNode,
    SlotName,
    TimeFormat,
    TimeSpan,
    UnitFormat,
    UserService,
} from '@frontend/vanilla/core';
import { CurrentSessionConfig } from '@frontend/vanilla/shared/current-session';
import { firstValueFrom } from 'rxjs';

import { LoginDurationTrackingService } from './login-duration-tracking.service';
import { LoginDurationConfig } from './login-duration.client-config';

/**
 * @stable
 *
 * Sitecore item: {@link http://cms.prod.env.works/sitecore/DirectLink.aspx?fo={05EB8D98-C673-44BA-8D72-30E2109BCD57}&la=}
 */
@Component({
    imports: [CommonModule, DynamicHtmlDirective],
    selector: 'vn-login-duration',
    templateUrl: './login-duration.html',
    host: { class: 'login-duration-container' },
    styleUrls: ['../../../../../themepark/themes/whitelabel/components/login-duration/styles.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginDurationComponent implements OnInit, OnDestroy {
    /**
     * Provide a placeholder message to prevent a whole-page layout shift.
     */
    readonly PLACEHOLDER_MESSAGE = '&nbsp;';

    private elapsedTimeSpan = TimeSpan.ZERO;
    private htmlNodeClassName: string;

    private readonly clockService = inject(ClockService);
    private readonly currentSessionConfig = inject(CurrentSessionConfig);
    private readonly dynamicLayoutService = inject(DynamicLayoutService);
    private readonly htmlNode = inject(HtmlNode);
    private readonly loginDurationConfig = inject(LoginDurationConfig);
    private readonly trackingService = inject(LoginDurationTrackingService);
    private readonly userService = inject(UserService);

    readonly loginDurationHtml = this.clockService.tick<string | null>((elapsedTicks: number) => {
        if (!this.userService.isAuthenticated || !this.loginDurationConfig.text) {
            return null;
        }

        if (!this.loginDurationConfig.isConfigReady || !this.currentSessionConfig.isConfigReady) {
            return this.PLACEHOLDER_MESSAGE;
        }

        this.elapsedTimeSpan = TimeSpan.fromSeconds(elapsedTicks, this.currentSessionConfig.loginDuration ?? 0);

        const loginDuration = this.clockService.toTotalTimeStringFormat(this.elapsedTimeSpan, {
            unitFormat: UnitFormat.Hidden,
            hideZeros: false,
            timeFormat: this.loginDurationConfig.timeFormat || TimeFormat.HMS,
        });

        return this.loginDurationConfig.text.toLowerCase().replace('{duration}', `<span class="login-duration-time">${loginDuration}</span>`);
    });

    async ngOnInit() {
        if (!this.userService.isAuthenticated) {
            return;
        }

        await Promise.all([firstValueFrom(this.loginDurationConfig.whenReady), firstValueFrom(this.currentSessionConfig.whenReady)]);

        this.htmlNodeClassName = `${this.loginDurationConfig.slotName?.toLowerCase() ?? SlotName.Custom}-login-duration-shown`;

        if (this.currentSessionConfig.loginDuration != null) {
            this.htmlNode.setCssClass(this.htmlNodeClassName, true);
            this.trackingService.trackLoad(this.elapsedTimeSpan.toString(), this.loginDurationConfig.slotName || SlotName.Custom);
        }
    }

    ngOnDestroy() {
        this.htmlNode.setCssClass(this.htmlNodeClassName, false);

        if (!this.userService.isAuthenticated) {
            this.dynamicLayoutService.removeComponent(this.loginDurationConfig.slotName, LoginDurationComponent);
        }
    }
}
